import { apiClient } from './apiClient';
import type {
  JobEndDetail,
  EligibleDelegate,
  ActivityRating,
  FeedbackItemRequest,
  SkillRatingItem,
  SkillRatingScale,
  SkillDetail,
  ActiveJobEnd,
  ActiveJobEndsFilterRequest,
  PaginatedResponse,
  BulkActionResult,
} from './types/jobEnd.types';
import type { JobEndAuditRecord } from './types/audit.types';

export const jobEndApi = {
  getById: async (id: number): Promise<JobEndDetail> => {
    try {
      return await apiClient.get(`/job-ends/${id}`).then((res) => res.data);
    } catch (error) {
      console.error('Failed to fetch job end:', error);
      throw error;
    }
  },

  getEligibleDelegates: async (id: number): Promise<EligibleDelegate[]> => {
    try {
      return await apiClient
        .get(`/job-ends/${id}/eligible-delegates`)
        .then((res) => res.data);
    } catch (error) {
      console.error('Failed to fetch eligible delegates:', error);
      throw error;
    }
  },

  delegate: async (id: number, delegateStaffId: number, rowVersion?: string): Promise<JobEndDetail> => {
    try {
      const res = await apiClient.post(`/job-ends/${id}/delegate`, { delegateStaffId, rowVersion });
      return res.data;
    } catch (error) {
      console.error('Failed to delegate job end:', error);
      throw error;
    }
  },

  approveDelegation: async (id: number, rowVersion?: string): Promise<void> => {
    try {
      await apiClient.post(`/job-ends/${id}/delegation/approve`, { rowVersion });
    } catch (error) {
      console.error('Failed to approve delegation:', error);
      throw error;
    }
  },

  refuseDelegation: async (id: number, rowVersion?: string): Promise<void> => {
    try {
      await apiClient.post(`/job-ends/${id}/delegation/refuse`, { rowVersion });
    } catch (error) {
      console.error('Failed to refuse delegation:', error);
      throw error;
    }
  },

  updateActivityRatings: async (id: number, ratings: ActivityRating[], rowVersion?: string): Promise<JobEndDetail> => {
    try {
      const res = await apiClient.put(`/job-ends/${id}/activity-ratings`, { ratings, rowVersion });
      return res.data;
    } catch (error) {
      console.error('Failed to update activity ratings:', error);
      throw error;
    }
  },

  updateStrengthFeedbacks: async (id: number, items: FeedbackItemRequest[], rowVersion?: string): Promise<JobEndDetail> => {
    try {
      const res = await apiClient.put(`/job-ends/${id}/strengths`, { items, rowVersion });
      return res.data;
    } catch (error) {
      console.error('Failed to update strength feedbacks:', error);
      throw error;
    }
  },

  updateDevelopmentAreaFeedbacks: async (
    id: number,
    items: FeedbackItemRequest[],
    rowVersion?: string
  ): Promise<JobEndDetail> => {
    try {
      const res = await apiClient.put(`/job-ends/${id}/development-areas`, { items, rowVersion });
      return res.data;
    } catch (error) {
      console.error('Failed to update development area feedbacks:', error);
      throw error;
    }
  },

  getAuditHistory: async (id: number): Promise<JobEndAuditRecord[]> => {
    try {
      return await apiClient.get(`/job-ends/${id}/audit-history`).then((res) => res.data);
    } catch (error) {
      console.error('Failed to fetch audit history:', error);
      throw error;
    }
  },

  updateSkillRatings: async (id: number, ratings: SkillRatingItem[], rowVersion?: string): Promise<JobEndDetail> => {
    try {
      const res = await apiClient.put(`/job-ends/${id}/skill-ratings`, { ratings, rowVersion });
      return res.data;
    } catch (error) {
      console.error('Failed to update skill ratings:', error);
      throw error;
    }
  },

  updateOverallSkillRating: async (id: number, rating: SkillRatingScale | null, rowVersion?: string): Promise<JobEndDetail> => {
    try {
      const res = await apiClient.put(`/job-ends/${id}/overall-skill-rating`, { rating, rowVersion });
      return res.data;
    } catch (error) {
      console.error('Failed to update overall skill rating:', error);
      throw error;
    }
  },

  finalize: async (id: number, rowVersion?: string): Promise<JobEndDetail> => {
    try {
      const res = await apiClient.post(`/job-ends/${id}/finalize`, { rowVersion });
      return res.data;
    } catch (error) {
      console.error('Failed to finalize job end:', error);
      throw error;
    }
  },

  acknowledgeDelivery: async (id: number, rowVersion?: string): Promise<JobEndDetail> => {
    try {
      const res = await apiClient.post(`/job-ends/${id}/acknowledge-delivery`, { rowVersion });
      return res.data;
    } catch (error) {
      console.error('Failed to acknowledge delivery:', error);
      throw error;
    }
  },

  getSkillAreaDetails: async (jobEndId: number, areaId: number): Promise<SkillDetail[]> => {
    try {
      return await apiClient
        .get(`/job-ends/${jobEndId}/skill-areas/${areaId}`)
        .then((res) => res.data);
    } catch (error) {
      console.error('Failed to fetch skill area details:', error);
      throw error;
    }
  },

  // Admin methods

  getActiveJobEnds: async (
    filter: ActiveJobEndsFilterRequest
  ): Promise<PaginatedResponse<ActiveJobEnd>> => {
    try {
      const params = new URLSearchParams();
      params.append('page', String(filter.page));
      params.append('pageSize', String(filter.pageSize));
      params.append('sortBy', filter.sortBy);
      params.append('sortDirection', filter.sortDirection);
      if (filter.projectName) params.append('projectName', filter.projectName);
      if (filter.dueDateFrom) params.append('dueDateFrom', filter.dueDateFrom);
      if (filter.dueDateTo) params.append('dueDateTo', filter.dueDateTo);
      if (filter.performanceFlag) params.append('performanceFlag', filter.performanceFlag);
      if (filter.appraiseeName) params.append('appraiseeName', filter.appraiseeName);
      if (filter.geo) params.append('geo', filter.geo);
      if (filter.tenureInGrade) params.append('tenureInGrade', filter.tenureInGrade);
      if (filter.jobEndAuthor) params.append('jobEndAuthor', filter.jobEndAuthor);
      if (filter.status) params.append('status', filter.status);

      const res = await apiClient.get(`/job-ends/admin/active?${params.toString()}`);
      return res.data;
    } catch (error) {
      console.error('Failed to fetch active job ends:', error);
      throw error;
    }
  },

  editAuthor: async (id: number, newCollatorStaffId: number, rowVersion?: string): Promise<JobEndDetail> => {
    try {
      const res = await apiClient.post(`/job-ends/${id}/edit-author`, { newCollatorStaffId, rowVersion });
      return res.data;
    } catch (error) {
      console.error('Failed to edit job end author:', error);
      throw error;
    }
  },

  getEligibleAuthors: async (id: number): Promise<EligibleDelegate[]> => {
    try {
      return await apiClient
        .get(`/job-ends/${id}/eligible-authors`)
        .then((res) => res.data);
    } catch (error) {
      console.error('Failed to fetch eligible authors:', error);
      throw error;
    }
  },

  sendReminder: async (id: number): Promise<void> => {
    try {
      await apiClient.post(`/job-ends/${id}/send-reminder`);
    } catch (error) {
      console.error('Failed to send reminder:', error);
      throw error;
    }
  },

  reissue: async (id: number, rowVersion?: string): Promise<JobEndDetail> => {
    try {
      const res = await apiClient.post(`/job-ends/${id}/reissue`, { rowVersion });
      return res.data;
    } catch (error) {
      console.error('Failed to reissue job end:', error);
      throw error;
    }
  },

  reopen: async (id: number, rowVersion?: string): Promise<JobEndDetail> => {
    try {
      const res = await apiClient.post(`/job-ends/${id}/reopen`, { rowVersion });
      return res.data;
    } catch (error) {
      console.error('Failed to reopen job end:', error);
      throw error;
    }
  },

  deleteJobEnd: async (id: number): Promise<void> => {
    try {
      await apiClient.delete(`/job-ends/${id}`);
    } catch (error) {
      console.error('Failed to delete job end:', error);
      throw error;
    }
  },

  // Bulk actions

  bulkEditAuthor: async (jobEndIds: number[], newCollatorStaffId: number): Promise<BulkActionResult> => {
    try {
      const res = await apiClient.post('/job-ends/bulk/edit-author', { jobEndIds, newCollatorStaffId });
      return res.data;
    } catch (error) {
      console.error('Failed to bulk edit authors:', error);
      throw error;
    }
  },

  bulkSendReminder: async (jobEndIds: number[]): Promise<BulkActionResult> => {
    try {
      const res = await apiClient.post('/job-ends/bulk/send-reminder', { jobEndIds });
      return res.data;
    } catch (error) {
      console.error('Failed to bulk send reminders:', error);
      throw error;
    }
  },

  bulkReissue: async (jobEndIds: number[]): Promise<BulkActionResult> => {
    try {
      const res = await apiClient.post('/job-ends/bulk/reissue', { jobEndIds });
      return res.data;
    } catch (error) {
      console.error('Failed to bulk reissue:', error);
      throw error;
    }
  },

  bulkDelete: async (jobEndIds: number[]): Promise<BulkActionResult> => {
    try {
      const res = await apiClient.post('/job-ends/bulk/delete', { jobEndIds });
      return res.data;
    } catch (error) {
      console.error('Failed to bulk delete:', error);
      throw error;
    }
  },
};
